import { useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Activity,
  ArrowRight,
  CheckCircle2,
  Code2,
  Database,
  Play,
  Plus,
  RotateCcw,
} from 'lucide-react';
import { useBacktests, useDatasets, useStrategies, useTradingOverview } from '@/api/queries';
import type { BacktestRunDto, DatasetSummaryDto, StrategySummaryDto } from '@/api/types';
import type { TradingOverviewDto } from '@/api/trading';
import {
  formatDateTime,
  formatShortDate,
  formatSigned,
  getBacktestStatusTone,
  getNetPnl,
  getReturnPercent,
  isCertifiedRun,
  isActiveRun,
} from '@/components/backtests/backtestPresentation';
import { Badge } from '@/components/ui/Badge';
import { Button, ButtonLink } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { OverviewStat } from '@/components/ui/OverviewStat';
import styles from './DashboardPage.module.css';
import { strategyBuilderPath } from '@/lib/navigation';
import { routes } from '@/lib/routePaths';
import { useAuth } from '@/contexts/AuthContext';

interface DashboardViewModel {
  readyDatasetCount: number;
  datasetCount: number;
  strategyCount: number;
  activeRunCount: number;
  attentionCount: number;
  activeRuns: BacktestRunDto[];
  recentResults: BacktestRunDto[];
  attentionItems: AttentionItem[];
  recentDatasets: DatasetSummaryDto[];
  recentStrategies: StrategySummaryDto[];
  datasetNames: Map<string | null | undefined, string>;
}

type AttentionItem = {
  kind: 'dataset' | 'backtest';
  id: string | null | undefined;
  title: string;
  detail: string;
  timestamp: string | null | undefined;
};

export default function DashboardPage() {
  const { user } = useAuth();
  const canTrade = user?.role === 'Trader' || user?.role === 'Admin';
  const datasetsQuery = useDatasets();
  const strategiesQuery = useStrategies();
  const tradingQuery = useTradingOverview(false, { enabled: canTrade });
  const backtestsQuery = useBacktests({
    refetchInterval: (query) => query.state.data?.some(isActiveRun) ? 2_000 : false,
  });
  const view = useMemo(
    () => buildDashboardViewModel(
      datasetsQuery.data ?? [],
      strategiesQuery.data ?? [],
      backtestsQuery.data ?? [],
    ),
    [backtestsQuery.data, datasetsQuery.data, strategiesQuery.data],
  );
  const primaryAction = getOverviewPrimaryAction(
    user?.role,
    tradingQuery.data,
    datasetsQuery.data,
    strategiesQuery.data,
  );
  const PrimaryIcon = primaryAction.icon;

  return (
    <section className={styles.page} aria-labelledby="dashboard-title">
      <header className={styles.pageHeader}>
        <div>
          <p className={styles.eyebrow}>Workspace</p>
          <h1 id="dashboard-title">Overview</h1>
          <p className={styles.subtitle}>See what is ready, what is running, and what needs your attention.</p>
        </div>
        <ButtonLink variant="primary" to={primaryAction.to}>
          <PrimaryIcon size={16} /> {primaryAction.label}
        </ButtonLink>
      </header>

      <div className={styles.statsGrid} aria-label="Workspace summary">
        <OverviewStat
          label="Ready datasets"
          value={datasetsQuery.error || datasetsQuery.isLoading ? '—' : `${view.readyDatasetCount}/${view.datasetCount}`}
          hint={datasetsQuery.isLoading ? 'Loading datasets' : 'Market data available'}
          to={routes.datasets}
          icon={Database}
          tone={!datasetsQuery.error && !datasetsQuery.isLoading && view.datasetCount > 0 && view.readyDatasetCount === view.datasetCount ? 'success' : 'info'}
        />
        <OverviewStat
          label="Saved strategies"
          value={strategiesQuery.error || strategiesQuery.isLoading ? '—' : view.strategyCount.toLocaleString()}
          hint={strategiesQuery.isLoading ? 'Loading strategies' : 'Ready for evaluation'}
          to="/strategies"
          icon={Code2}
          tone="info"
        />
        <OverviewStat
          label="Active runs"
          value={backtestsQuery.error || backtestsQuery.isLoading ? '—' : view.activeRunCount.toLocaleString()}
          hint={backtestsQuery.isLoading ? 'Loading recent runs' : 'Queued or running'}
          to={routes.backtests}
          icon={Play}
          tone={backtestsQuery.error || backtestsQuery.isLoading ? 'info' : view.activeRunCount > 0 ? 'warning' : 'success'}
        />
        <OverviewStat
          label="Needs attention"
          value={datasetsQuery.error || backtestsQuery.error || datasetsQuery.isLoading || backtestsQuery.isLoading ? '—' : view.attentionCount.toLocaleString()}
          hint={datasetsQuery.error || backtestsQuery.error ? 'Some checks unavailable' : 'Failed data or recent runs'}
          to="#attention"
          icon={AlertTriangle}
          tone={datasetsQuery.error || backtestsQuery.error || datasetsQuery.isLoading || backtestsQuery.isLoading ? 'info' : view.attentionCount > 0 ? 'danger' : 'success'}
        />
      </div>

      {canTrade && <Card className={styles.liveOperations}>
        <PanelHeader title="Live operations" action={<Link to={routes.trading}>Open operations <ArrowRight size={14} /></Link>} />
        {tradingQuery.error ? (
          <SourceError message="Trading status is unavailable. Research tools remain available." onRetry={() => void tradingQuery.refetch()} />
        ) : tradingQuery.isLoading ? (
          <LoadingState label="Loading live operations" compact />
        ) : (
          <div className={styles.liveMetrics}>
            <LiveMetric label="Connected accounts" value={tradingQuery.data?.connectedAccounts ?? 0} />
            <LiveMetric label="Active deployments" value={tradingQuery.data?.activeDeployments ?? 0} />
            <LiveMetric label="Open positions" value={tradingQuery.data?.openPositions ?? 0} />
            <LiveMetric label="Needs attention" value={tradingQuery.data?.attentionRequired ?? 0} attention />
          </div>
        )}
      </Card>}

      <div className={styles.primaryGrid}>
        <Card className={styles.activityCard}>
          <PanelHeader title="Backtest activity" action={<Link to={routes.backtests}>View all <ArrowRight size={14} /></Link>} />
          <p className={styles.scopeNote}>Activity is based on the 100 most recent runs.</p>

          {backtestsQuery.error ? (
            <SourceError message="Backtest activity is unavailable." onRetry={() => void backtestsQuery.refetch()} />
          ) : backtestsQuery.isLoading ? (
            <LoadingState label="Loading backtest activity" />
          ) : backtestsQuery.data?.length === 0 ? (
            <EmptyState
              title="No backtests yet"
              description="Run a saved strategy against a ready dataset to see activity here."
              action={<ButtonLink variant="outline-info" to={routes.createBacktest()}>New backtest</ButtonLink>}
            />
          ) : (
            <div className={styles.activitySections}>
              <ActivityGroup title="Active runs" count={view.activeRunCount}>
                {view.activeRuns.length > 0 ? view.activeRuns.map((run) => (
                  <ActiveRunRow key={run.id ?? `${run.datasetId}-${run.createdUtc}`} run={run} datasetName={dashboardDatasetSummary(run, view.datasetNames)} />
                )) : <QuietState>Nothing is queued or running.</QuietState>}
              </ActivityGroup>
              <ActivityGroup title="Recent results" count={view.recentResults.length}>
                {view.recentResults.length > 0 ? view.recentResults.map((run) => (
                  <ResultRow key={run.id ?? `${run.datasetId}-${run.createdUtc}`} run={run} datasetName={dashboardDatasetSummary(run, view.datasetNames)} />
                )) : <QuietState>No successful runs yet.</QuietState>}
              </ActivityGroup>
            </div>
          )}
        </Card>

        <Card className={styles.attentionCard}>
          <PanelHeader title="Needs attention" id="attention" />
          {(datasetsQuery.error || backtestsQuery.error) && (
            <div className={styles.partialNotice}>
              Some checks are unavailable.
              <button type="button" onClick={() => void Promise.all([datasetsQuery.refetch(), backtestsQuery.refetch()])}>Retry</button>
            </div>
          )}
          {view.attentionItems.length > 0 ? (
            <div className={styles.attentionList}>
              {view.attentionItems.map((item, index) => (
                <AttentionRow key={`${item.kind}-${item.id ?? index}`} item={item} />
              ))}
              {(datasetsQuery.isLoading || backtestsQuery.isLoading) && (
                <p className={styles.checkingState}>Checking remaining workspace health…</p>
              )}
            </div>
          ) : datasetsQuery.isLoading || backtestsQuery.isLoading ? (
            <LoadingState label="Checking workspace health" compact />
          ) : !datasetsQuery.error && !backtestsQuery.error ? (
            <div className={styles.allClear}>
              <CheckCircle2 size={24} aria-hidden="true" />
              <strong>All clear</strong>
              <span>No failed datasets or recent backtests.</span>
            </div>
          ) : null}
        </Card>
      </div>

      <div className={styles.continueGrid}>
        <Card>
          <PanelHeader title="Recent datasets" action={<Link to={routes.datasets}>View datasets <ArrowRight size={14} /></Link>} />
          {datasetsQuery.error ? (
            <SourceError message="Datasets are unavailable." onRetry={() => void datasetsQuery.refetch()} />
          ) : datasetsQuery.isLoading ? (
            <LoadingState label="Loading recent datasets" compact />
          ) : view.recentDatasets.length > 0 ? (
            <div className={styles.workList}>
              {view.recentDatasets.map((dataset) => <DatasetRow key={dataset.id} dataset={dataset} />)}
            </div>
          ) : (
            <EmptyState
              title="No datasets"
              description="Import candles and news data to start testing."
              action={<Link className={styles.textAction} to={routes.createDataset()}>Add dataset <ArrowRight size={14} /></Link>}
            />
          )}
        </Card>

        <Card>
          <PanelHeader title="Recently updated strategies" action={<Link to="/strategies">View strategies <ArrowRight size={14} /></Link>} />
          {strategiesQuery.error ? (
            <SourceError message="Strategies are unavailable." onRetry={() => void strategiesQuery.refetch()} />
          ) : strategiesQuery.isLoading ? (
            <LoadingState label="Loading recent strategies" compact />
          ) : view.recentStrategies.length > 0 ? (
            <div className={styles.workList}>
              {view.recentStrategies.map((strategy) => <StrategyRow key={strategy.id} strategy={strategy} />)}
            </div>
          ) : (
            <EmptyState
              title="No saved strategies"
              description="Build a strategy before creating a backtest."
              action={<Link className={styles.textAction} to={strategyBuilderPath()}>Create strategy <ArrowRight size={14} /></Link>}
            />
          )}
        </Card>
      </div>

    </section>
  );
}

type PrimaryAction = { label: string; to: string; icon: typeof Plus };

export function getOverviewPrimaryAction(
  role: 'User' | 'Trader' | 'Admin' | undefined,
  trading: TradingOverviewDto | undefined,
  datasets: readonly DatasetSummaryDto[] | undefined,
  strategies: readonly StrategySummaryDto[] | undefined,
): PrimaryAction {
  if ((role === 'Trader' || role === 'Admin') && (trading?.attentionRequired ?? 0) > 0) {
    return { label: 'Review trading issues', to: `${routes.trading}?status=attention`, icon: AlertTriangle };
  }
  if ((role === 'Trader' || role === 'Admin') && (trading?.activeDeployments ?? 0) > 0) {
    return { label: 'Open trading operations', to: routes.trading, icon: Activity };
  }
  if (datasets && !datasets.some((dataset) => dataset.status === 'Ready')) {
    return { label: 'Add dataset', to: routes.createDataset(), icon: Database };
  }
  if (strategies && strategies.length === 0) {
    return { label: 'Create strategy', to: routes.newStrategy, icon: Code2 };
  }
  return { label: 'New backtest', to: routes.createBacktest(), icon: Plus };
}

export function buildDashboardViewModel(
  datasets: readonly DatasetSummaryDto[],
  strategies: readonly StrategySummaryDto[],
  backtests: readonly BacktestRunDto[],
): DashboardViewModel {
  const datasetNames = new Map(
    datasets.map((dataset) => [dataset.id, dataset.displayName ?? dataset.id ?? 'Unknown dataset']),
  );
  const activeRuns = newest(backtests.filter(isActiveRun), runTime).slice(0, 4);
  const recentResults = newest(backtests.filter((run) => run.status === 'Succeeded'), runTime).slice(0, 5);
  const failedDatasets = datasets.filter((dataset) => dataset.status === 'Failed');
  const failedRuns = backtests.filter((run) => run.status === 'Failed');
  const attentionItems: AttentionItem[] = [
    ...failedDatasets.map((dataset): AttentionItem => ({
      kind: 'dataset',
      id: dataset.id,
      title: dataset.displayName ?? 'Unnamed dataset',
      detail: dataset.importErrors?.[0] ?? 'Dataset import failed.',
      timestamp: dataset.createdUtc,
    })),
    ...failedRuns.map((run): AttentionItem => ({
      kind: 'backtest',
      id: run.id,
      title: run.strategyName ?? 'Unnamed strategy',
      detail: run.error ?? 'Backtest failed without an error message.',
      timestamp: run.completedUtc ?? run.createdUtc,
    })),
  ];

  return {
    readyDatasetCount: datasets.filter((dataset) => dataset.status === 'Ready').length,
    datasetCount: datasets.length,
    strategyCount: strategies.length,
    activeRunCount: backtests.filter(isActiveRun).length,
    attentionCount: failedDatasets.length + failedRuns.length,
    activeRuns,
    recentResults,
    attentionItems: newest(attentionItems, (item) => item.timestamp).slice(0, 5),
    recentDatasets: newest(datasets, (dataset) => dataset.createdUtc).slice(0, 4),
    recentStrategies: newest(strategies, (strategy) => strategy.updatedUtc).slice(0, 4),
    datasetNames,
  };
}

function newest<T>(items: readonly T[], getDate: (item: T) => string | null | undefined) {
  return [...items].sort((left, right) => timestamp(getDate(right)) - timestamp(getDate(left)));
}

function timestamp(value: string | null | undefined) {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function dashboardDatasetSummary(run: BacktestRunDto, names: ReadonlyMap<string | null | undefined, string>) {
  const ids = run.datasetIds?.length ? run.datasetIds : run.datasetId ? [run.datasetId] : [];
  if (ids.length === 1) return names.get(ids[0]) ?? ids[0];
  return ids.length > 1 ? `${ids.length} datasets` : undefined;
}

function runTime(run: BacktestRunDto) {
  return run.completedUtc ?? run.startedUtc ?? run.createdUtc;
}

function PanelHeader({ title, action, id }: { title: string; action?: ReactNode; id?: string }) {
  return (
    <div className={styles.panelHeader} id={id}>
      <h2>{title}</h2>
      {action && <div className={styles.panelAction}>{action}</div>}
    </div>
  );
}

function ActivityGroup({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <section className={styles.activityGroup}>
      <div className={styles.groupTitle}>
        <h3>{title}</h3>
        <span>{count}</span>
      </div>
      <div className={styles.runList}>{children}</div>
    </section>
  );
}

function ActiveRunRow({ run, datasetName }: { run: BacktestRunDto; datasetName?: string }) {
  const progress = Math.max(0, Math.min(100, Math.round((run.progress ?? 0) * 100)));
  return (
    <Link className={styles.runRow} to={run.id ? routes.backtest(run.id) : routes.backtests}>
      <div className={styles.rowIdentity}>
        <strong>{run.strategyName ?? 'Unnamed strategy'}</strong>
        <span>{datasetName ?? run.datasetId ?? 'Unknown dataset'}</span>
      </div>
      <div className={styles.progressCell}>
        <div className={styles.statusLine}>
          <Badge variant={getBacktestStatusTone(run.status)}>{run.status ?? 'Unknown'}</Badge>
          <span>{progress}%</span>
        </div>
        <div className={styles.progressTrack} role="progressbar" aria-label={`${run.strategyName ?? 'Backtest'} progress`} aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${progress}%` }} />
        </div>
      </div>
      <ArrowRight className={styles.rowArrow} size={15} aria-hidden="true" />
    </Link>
  );
}

function ResultRow({ run, datasetName }: { run: BacktestRunDto; datasetName?: string }) {
  const netPnl = getNetPnl(run);
  const returnPercent = getReturnPercent(run);
  return (
    <Link className={styles.runRow} to={run.id ? routes.backtest(run.id) : routes.backtests}>
      <div className={styles.rowIdentity}>
        <strong>{run.strategyName ?? 'Unnamed strategy'}</strong>
        <span>{datasetName ?? run.datasetId ?? 'Unknown dataset'} · {formatShortDate(run.completedUtc ?? run.createdUtc)}</span>
      </div>
      <div className={styles.resultMetrics}>
        <span className={returnPercent != null && returnPercent < 0 ? styles.negative : styles.positive}>
          {returnPercent == null ? '—' : `${formatSigned(returnPercent)}%`}
        </span>
        <small>
          {netPnl == null ? '—' : `${formatSigned(netPnl)} net`} · {run.totalTrades ?? 0} completed trades · {run.maxDrawdown == null ? '—' : run.maxDrawdown.toFixed(2)} DD
          {run.maxDrawdownPercent == null ? '' : ` (${run.maxDrawdownPercent.toFixed(2)}%)`}
          {!isCertifiedRun(run)
            ? (run.executionErrorCount ?? 0) > 0
              ? ` · ${run.executionErrorCount} execution error(s)`
              : ' · legacy execution model'
            : ''}
        </small>
      </div>
      <ArrowRight className={styles.rowArrow} size={15} aria-hidden="true" />
    </Link>
  );
}

function AttentionRow({ item }: { item: AttentionItem }) {
  const to = item.kind === 'dataset'
    ? item.id ? routes.dataset(item.id) : routes.datasets
    : item.id ? routes.backtest(item.id) : routes.backtests;
  return (
    <Link className={styles.attentionRow} to={to}>
      <AlertTriangle size={17} aria-hidden="true" />
      <div>
        <span className={styles.itemType}>{item.kind}</span>
        <strong>{item.title}</strong>
        <p title={item.detail}>{item.detail}</p>
        <small>{formatDateTime(item.timestamp)}</small>
      </div>
      <ArrowRight size={14} aria-hidden="true" />
    </Link>
  );
}

function DatasetRow({ dataset }: { dataset: DatasetSummaryDto }) {
  const coverage = dataset.fromUtc && dataset.toUtc
    ? `${formatShortDate(dataset.fromUtc)} – ${formatShortDate(dataset.toUtc)}`
    : 'Coverage unavailable';
  return (
    <Link className={styles.workRow} to={dataset.id ? routes.dataset(dataset.id) : routes.datasets}>
      <div className={styles.rowIdentity}>
        <strong>{dataset.displayName ?? 'Unnamed dataset'}</strong>
        <span>{dataset.instrument ?? 'Unknown instrument'} · {coverage}</span>
      </div>
      <Badge variant={dataset.status === 'Ready' ? 'success' : 'danger'}>{dataset.status ?? 'Unknown'}</Badge>
      <ArrowRight className={styles.rowArrow} size={15} aria-hidden="true" />
    </Link>
  );
}

function StrategyRow({ strategy }: { strategy: StrategySummaryDto }) {
  const timeframeCount = strategy.timeframes?.length ?? 0;
  return (
    <Link className={styles.workRow} to={strategyBuilderPath(strategy.id)}>
      <div className={styles.rowIdentity}>
        <strong>{strategy.name}</strong>
        <span>{strategy.nodeCount} nodes · {timeframeCount} timeframe{timeframeCount === 1 ? '' : 's'}</span>
      </div>
      <time dateTime={strategy.updatedUtc}>{formatShortDate(strategy.updatedUtc)}</time>
      <ArrowRight className={styles.rowArrow} size={15} aria-hidden="true" />
    </Link>
  );
}

function SourceError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className={styles.sourceError} role="alert">
      <AlertTriangle size={18} aria-hidden="true" />
      <span>{message}</span>
      <Button type="button" variant="outline-info" onClick={onRetry}><RotateCcw size={14} /> Retry</Button>
    </div>
  );
}

function LoadingState({ label, compact = false }: { label: string; compact?: boolean }) {
  return (
    <div className={`${styles.loadingState}${compact ? ` ${styles.loadingStateCompact}` : ''}`} role="status">
      <span className={styles.loadingPulse} />
      <span>{label}…</span>
    </div>
  );
}

function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return (
    <div className={styles.emptyState}>
      <strong>{title}</strong>
      <span>{description}</span>
      {action}
    </div>
  );
}

function QuietState({ children }: { children: ReactNode }) {
  return <p className={styles.quietState}>{children}</p>;
}

function LiveMetric({ label, value, attention = false }: { label: string; value: number; attention?: boolean }) {
  return <Link className={`${styles.liveMetric}${attention && value > 0 ? ` ${styles.liveMetricAttention}` : ''}`} to={routes.trading}>
    <span>{label}</span><strong>{value.toLocaleString()}</strong>
  </Link>;
}
